// ===== frontend/src/components/Upload/PDFUploader.jsx (Updated with session storage) =====
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { uploadPDF } from '../../utils/api';

const PDFUploader = () => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  // File validation
  const validateFile = (file) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['application/pdf'];

    if (!allowedTypes.includes(file.type)) {
      return 'Please select a PDF file.';
    }

    if (file.size > maxSize) {
      return 'File is too large. Maximum: 10MB.';
    }

    return null;
  };

  // Handle file upload to backend
  const uploadFile = async (file) => {
    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      console.log('Starting upload for file:', file.name);
      
      const result = await uploadPDF(file);
      
      clearInterval(progressInterval);
      setUploadProgress(100);

      console.log('Upload successful:', result);
      
      // Store form structure in localStorage for the session
      localStorage.setItem(`form_structure_${result.sessionId}`, JSON.stringify(result));
      localStorage.setItem(`form_data_${result.sessionId}`, JSON.stringify({}));
      
      // Success - navigate to form page with session ID
      setTimeout(() => {
        navigate(`/form/${result.sessionId}`);
      }, 1000);

    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Upload failed');
      setUploadProgress(0);
    } finally {
      setIsUploading(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (file) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setUploadedFile(file);
    uploadFile(file);
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // File input change handler
  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // Click to upload
  const handleClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  // Reset upload
  const handleReset = () => {
    setUploadedFile(null);
    setError(null);
    setUploadProgress(0);
    setIsUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Upload Area */}
      <div
        style={{
          position: 'relative',
          border: `2px dashed ${isDragOver ? '#2563eb' : '#d1d5db'}`,
          borderRadius: '0.75rem',
          padding: '3rem',
          textAlign: 'center',
          cursor: isUploading ? 'default' : 'pointer',
          transition: 'all 0.2s',
          backgroundColor: isDragOver ? '#eff6ff' : 'white',
          opacity: isUploading ? 0.75 : 1,
        }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        aria-label="Upload PDF file"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf"
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
          aria-hidden="true"
        />

        {/* Upload States */}
        {!isUploading && !uploadedFile && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{
              width: '4rem',
              height: '4rem',
              backgroundColor: '#f3f4f6',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto'
            }}>
              <svg style={{ width: '2rem', height: '2rem', color: '#9ca3af' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <div>
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem' }}>
                Upload PDF Form
              </h3>
              <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
                Drag your PDF file here or click to select
              </p>
              <p style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                Supported: PDF files up to 10MB
              </p>
            </div>
          </div>
        )}

        {/* Uploading State */}
        {isUploading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{
              width: '4rem',
              height: '4rem',
              backgroundColor: '#dbeafe',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto'
            }}>
              <div style={{
                width: '2rem',
                height: '2rem',
                border: '2px solid #2563eb',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
            </div>
            <div>
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem' }}>
                Processing PDF...
              </h3>
              <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
                {uploadedFile?.name}
              </p>
              
              {/* Progress Bar */}
              <div style={{ width: '100%', backgroundColor: '#e5e7eb', borderRadius: '9999px', height: '0.5rem', marginBottom: '0.5rem' }}>
                <div 
                  style={{ 
                    backgroundColor: '#2563eb', 
                    height: '0.5rem', 
                    borderRadius: '9999px', 
                    transition: 'width 0.3s',
                    width: `${uploadProgress}%`
                  }}
                ></div>
              </div>
              <p style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                {uploadProgress}% complete
              </p>
            </div>
          </div>
        )}

        {/* Success State */}
        {uploadProgress === 100 && !error && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{
              width: '4rem',
              height: '4rem',
              backgroundColor: '#dcfce7',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto'
            }}>
              <svg style={{ width: '2rem', height: '2rem', color: '#16a34a' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#14532d', marginBottom: '0.5rem' }}>
                Upload successful!
              </h3>
              <p style={{ color: '#15803d' }}>
                Redirecting to form...
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.5rem', padding: '1rem' }} role="alert">
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            <svg style={{ width: '1.25rem', height: '1.25rem', color: '#f87171', marginTop: '0.125rem', marginRight: '0.75rem', flexShrink: 0 }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div style={{ flex: 1 }}>
              <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#991b1b', marginBottom: '0.25rem' }}>
                Upload failed
              </h4>
              <p style={{ fontSize: '0.875rem', color: '#b91c1c' }}>
                {error}
              </p>
            </div>
          </div>
          <div style={{ marginTop: '1rem' }}>
            <button
              onClick={handleReset}
              style={{
                backgroundColor: '#fee2e2',
                color: '#991b1b',
                padding: '0.5rem 1rem',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#fecaca'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#fee2e2'}
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {/* Help Text */}
      <div style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '0.5rem', padding: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          <svg style={{ width: '1.25rem', height: '1.25rem', color: '#60a5fa', marginTop: '0.125rem', marginRight: '0.75rem', flexShrink: 0 }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1e40af', marginBottom: '0.25rem' }}>
              What happens after upload?
            </h4>
            <ul style={{ fontSize: '0.875rem', color: '#1d4ed8', listStyle: 'none', padding: 0, margin: 0 }}>
              <li style={{ marginBottom: '0.25rem' }}>• Your PDF is analyzed and all form fields are extracted</li>
              <li style={{ marginBottom: '0.25rem' }}>• German labels are automatically translated</li>
              <li style={{ marginBottom: '0.25rem' }}>• A user-friendly web form is created</li>
              <li>• You can optionally use AI assistance for filling out forms</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Add CSS for spin animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default PDFUploader;